import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Alert, Heading, Segmented, Stack, Text } from '@ui'
import { APP_NAME } from '../brand.js'
import PhotoMap from '../components/PhotoMap.jsx'
import { PhotoMonths, PhotoUrlContext, PhotoViewer, walkCaption } from '../components/Photos.jsx'
import { dayRange } from '../components/ShareControl.jsx'
import { formatDate, formatTime } from '../format.js'

// A link to the Photos page (/s/<token> of kind 'photos'; cmd/server/photoshares.go): the photos
// of the walks in a range of days, by date or on a map, read-only. Everything comes from the
// reply SharedWalk.jsx fetched, which has already left out what the owner chose to hide: with
// details hidden, photos near each walk's start or finish, and every time of day (a photo then
// carries only its walk's day). Walks are numbered within the reply and aren't links.

const when = (iso) => `${formatDate(iso)}, ${formatTime(iso)}`
const VIEWS = [{ value: 'date', label: 'By date' }, { value: 'map', label: 'On the map' }]

// "12 photos and 2 videos from 5 walks"
function countLabel(photos) {
  const n = (k, word) => `${k.toLocaleString('en-GB')} ${word}${k === 1 ? '' : 's'}`
  const videos = photos.filter((p) => p.media_type === 'video').length
  const walks = new Set(photos.map((p) => p.walk_id)).size
  const what = [photos.length - videos && n(photos.length - videos, 'photo'), videos && n(videos, 'video')].filter(Boolean).join(' and ')
  return `${what} from ${n(walks, 'walk')}`
}

export default function SharedPhotos({ reply, token }) {
  const { share } = reply
  const hidden = share.hide_details
  const [params, setParams] = useSearchParams()
  const view = params.get('view') === 'map' ? 'map' : 'date'
  const setView = (v) => setParams((p) => {
    if (v === 'map') p.set('view', 'map')
    else p.delete('view')
    return p
  }, { replace: true })
  const [index, setIndex] = useState(null)
  // The Photos page's shapes: walk_id is the walk's number in this reply; with details hidden
  // start_time stands for the walk's day only (noon UTC, never shown as a time).
  const photos = useMemo(
    () => reply.photos.map((p) => ({ ...p, walk_id: p.walk, start_time: p.start_time ?? `${p.date}T12:00:00Z` })),
    [reply],
  )
  const places = useMemo(
    () => new Map(photos.filter((p) => p.lat != null).map((p) => [p.id, { id: p.id, lat: p.lat, lon: p.lon, from: 'route' }])),
    [photos],
  )
  const caption = (w) => {
    if (!hidden) return walkCaption(w)
    return w.walk_name ? `${w.walk_name} · ${formatDate(w.start_time)}` : `Walk on ${formatDate(w.start_time)}`
  }
  const photoUrl = useMemo(() => (id, size) => `/api/share/${encodeURIComponent(token)}/photos/${id}/${size}`, [token])

  return (
    <PhotoUrlContext.Provider value={photoUrl}>
      <Stack wrap align="center" gap={2} mb={1}>
        <Heading size={3} mb={0} me="auto">Photos, {dayRange(share.from, share.to)}</Heading>
        {photos.length > 0 && <Segmented name="shared-photos-view" size="sm" value={view} onChange={setView} options={VIEWS} />}
      </Stack>
      <Text as="p" muted mb={3}>
        {photos.length ? `${countLabel(photos)}.` : 'No photos to show.'}
        {hidden && ` Photos taken near the start or finish of a walk aren't shown, nor the times of day.`}
      </Text>
      {reply.ended && (
        <Alert variant="info" compact>
          {reply.ended.status === 'expired' ? `This link expired on ${when(reply.ended.at)}` : `This link was stopped on ${when(reply.ended.at)}`}.
          You can still see the photos because you're signed in; anyone else is told the link no longer works.
        </Alert>
      )}

      {view === 'map' && photos.length > 0 && (
        <PhotoMap photos={photos} places={places} resetKey="shared" tiles="osm" readOnly caption={caption} walkLabel={caption} />
      )}
      {view === 'date' && (
        <>
          <PhotoMonths photos={photos} onOpen={setIndex} caption={caption} linkWalks={false} />
          <PhotoViewer photos={photos} index={index} onIndex={setIndex} walkLabel={caption} readOnly />
        </>
      )}

      <Text as="p" muted text="small" mt={4} mb={0}>
        Shared from {APP_NAME}. {share.expires_at && !reply.ended ? `This link works until ${when(share.expires_at)}.` : ''}
      </Text>
    </PhotoUrlContext.Provider>
  )
}
